/**
 * Scope (all-classes vs one class) + tab (sub-page) model shared by the sidemenu and class selector.
 * Switching scope keeps the current tab when the target has it, else falls back to assignments.
 */
export type NavScope = {kind: 'all'} | {kind: 'class'; classId: string};

export type NavTab =
  | 'assignments'
  | 'subjects'
  | 'diagnostics'
  | 'folders'
  | 'overview'
  | 'people'
  | 'settings';

const CLASS_PATH = /^\/classes\/([^/]+)/;

export const getNavScope = (pathname: string): NavScope => {
  const match = pathname.match(CLASS_PATH);
  // /classes and /classes/archived are the cross-class list, not a class scope.
  if (match && match[1] && match[1] !== 'archived') {
    return {kind: 'class', classId: match[1]};
  }
  return {kind: 'all'};
};

export const getNavTab = (pathname: string): NavTab | null => {
  if (pathname.includes('/people')) return 'people';
  if (pathname.includes('/settings')) return 'settings';
  if (pathname.includes('/students')) return 'overview';
  if (pathname.includes('/subjects')) return 'subjects';
  if (pathname.includes('/diagnostics')) return 'diagnostics';
  if (
    pathname === '/saved' ||
    pathname.startsWith('/saved/') ||
    pathname === '/folders' ||
    pathname.startsWith('/folders/')
  ) {
    return 'folders';
  }
  // Last so /my-assignments (no "/assignments" substring) and the above win first.
  if (pathname.includes('/assignments')) return 'assignments';
  return null;
};

const ALL_ROUTES: Partial<Record<NavTab, string>> = {
  assignments: '/assignments',
  subjects: '/subjects/class-subjects',
  diagnostics: '/diagnostics',
  folders: '/folders'
};

const getClassRoutes = (classId: string): Partial<Record<NavTab, string>> => ({
  overview: `/classes/${classId}/students/overview`,
  assignments: `/classes/${classId}/assignments`,
  subjects: `/classes/${classId}/subjects`,
  people: `/classes/${classId}/people`,
  settings: `/classes/${classId}/settings/details`
});

export const resolveScopeRoute = (target: NavScope, currentTab: NavTab | null): string => {
  const routes = target.kind === 'all' ? ALL_ROUTES : getClassRoutes(target.classId);
  return (currentTab && routes[currentTab]) || routes.assignments!;
};
